import { useMemo, useRef, useState } from 'react'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import Modal from '../../../components/common/Modal'
import StraatOpmerkingModal from '../StraatOpmerkingModal'
import useDebouncedValue from '../../../hooks/useDebouncedValue'
import { compareAddresses } from '../../../helpers/wervingsacties/groupAddressesByStreet'
import { groupSelectionState } from '../../../helpers/wervingsacties/groupSelectionState'
import { planDeselect } from '../../../helpers/wervingsacties/selectionPlan'
import { straatKey, straatOpmerking } from '../../../helpers/wervingsacties/straatKey'

// Stap 3 — de adressen in de gekozen wijken als wijk › buurt › straat ›
// adres, met op elk niveau een vinkje. De adressen zelf komen uit PDOK via
// useWijkAdressen in Detail.jsx, zodat ze niet bij elk bezoek aan dit
// tabblad opnieuw worden opgehaald.
//
// Eén adres per klik was onwerkbaar: Zuidwijken in Zutphen heeft 3.550
// adressen, Braamkamp alleen al 587. Een groep kiezen of loslaten gaat via
// Detail.jsx (plan + batches); dit scherm bewaakt drie dingen:
//  - een groepsvinkje werkt pas als de hele lijst binnen is, anders kies je
//    een halve wijk zonder het te weten;
//  - er loopt maar één actie tegelijk;
//  - loslaten van rijen waar al iets in staat gaat niet ongevraagd.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10)
// AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading

const nl = new Intl.NumberFormat('nl-NL')

/**
 * @typedef {'none' | 'some' | 'all'} SelectieStand
 */

/**
 * @typedef {object} Melding
 * @property {'select' | 'deselect'} soort
 * @property {string} tekst - Foutmelding van Firestore
 */

/**
 * @param {object} a - Adres of registratierij
 * @returns {string} Bijvoorbeeld '12A · 7206HA'
 */
const adresLabel = (a) => {
    const nr = [a.Huisnummer, a.HuisnummerToevoeging].filter(Boolean).join('')
    return a.Postcode ? `${nr} · ${a.Postcode}` : nr
}

/**
 * @param {object} a - Adres of registratierij
 * @returns {string} Bijvoorbeeld 'Braamkamp 12A · 7206HA'
 */
const fullAdresLabel = (a) => {
    const nr = [a.Huisnummer, a.HuisnummerToevoeging].filter(Boolean).join('')
    const head = [a.Straatnaam, nr].filter(Boolean).join(' ')
    return a.Postcode ? `${head} · ${a.Postcode}` : head
}

/**
 * De tekst waarin gezocht wordt, in kleine letters.
 *
 * @param {object} a - Adres of registratierij
 * @returns {string}
 */
const zoekTekst = (a) => `${a.Straatnaam || ''} ${a.Huisnummer || ''}${a.HuisnummerToevoeging || ''} ${a.Postcode || ''}`.toLowerCase()

// Zoeken wacht tot je even stopt met typen: een brede zoekterm klapt
// honderden straten open, en dat hoort niet bij elke aanslag te gebeuren.
const ZOEK_WACHTTIJD_MS = 200

const ARIA_CHECKED = { none: 'false', some: 'mixed', all: 'true' }

/**
 * Een vinkje met drie standen. Een knop en geen `<input type="checkbox">`: de
 * globale `input`-regel in de huisstijl sloopt checkboxes, en een knop kan
 * 'deels' aangeven (`aria-checked="mixed"`).
 *
 * @param {object} props
 * @param {SelectieStand} props.state - Leeg, deels of alles gekozen
 * @param {string} props.label - Toegankelijke naam, bijvoorbeeld 'Hele straat Braamkamp'
 * @param {boolean} props.disabled - Uit, bijvoorbeeld zolang de lijst laadt
 * @param {() => void} props.onClick - Wordt aangeroepen bij een klik
 */
const TriCheckbox = ({ state, label, disabled, onClick }) => {
    const Icon = state === 'all' ? CheckBoxIcon : state === 'some' ? IndeterminateCheckBoxIcon : CheckBoxOutlineBlankIcon
    return (
        <button
            type='button'
            role='checkbox'
            aria-checked={ARIA_CHECKED[state]}
            aria-label={label}
            className={`wa-check wa-check-${state}`}
            disabled={disabled}
            onClick={onClick}
        >
            <Icon aria-hidden='true' />
        </button>
    )
}

/**
 * @param {object} props
 * @param {number} props.selected - Aantal gekozen
 * @param {number} props.total - Aantal in de groep
 */
const Telling = ({ selected, total }) => (
    <span className='wa-count'>{`${nl.format(selected)} van ${nl.format(total)}`}</span>
)

/**
 * @param {object} props
 * @param {Array<object>} props.wijken - De gekozen wijken van de actie ({ WijkCode, Naam })
 * @param {Array<object>} props.registraties - De levende Wervingsregistraties van de actie
 * @param {Array<object>} props.adressen - Alle adressen in de gekozen wijken (useWijkAdressen)
 * @param {'idle' | 'loading' | 'complete' | 'failed'} props.adressenStand - Hoe ver het laden is; groepsvinkjes alleen bij 'complete'
 * @param {Object<string, string>} props.buurtNames - Buurtcode → buurtnaam (useBuurtNames); leeg zolang hij laadt
 * @param {Object<string, { Tekst: string }>} props.straatOpmerkingen - Wervingsacties.StraatOpmerkingen, per straatKey
 * @param {(key: string, tekst: string) => Promise<void>} props.onSaveOpmerking - Slaat de opmerking bij een straat op; gooit bij een fout
 * @param {(addresses: Array<object>, onProgress: (written: number, total: number) => void) => Promise<object>} props.onSelect - Voegt adressen toe; gooit bij een mislukte opslag
 * @param {(addresses: Array<object>, onProgress: (written: number, total: number) => void) => Promise<object>} props.onDeselect - Laat adressen los (zacht verwijderen); gooit bij een fout
 * @param {boolean} props.canEdit - Of de gebruiker het recht 'Huishoudens bewerken' heeft
 */
const AddressSelect = ({ wijken, registraties, adressen, adressenStand, buurtNames, straatOpmerkingen, onSaveOpmerking, onSelect, onDeselect, canEdit }) => {
    const options = adressen
    const loadState = adressenStand
    const [query, setQuery] = useState('')
    const zoekterm = useDebouncedValue(query, ZOEK_WACHTTIJD_MS)
    const [expanded, setExpanded] = useState(() => new Set())

    const [busy, setBusy] = useState(false)
    // De ref stopt een tweede klik vóórdat React opnieuw rendert; de state
    // zet de vinkjes zichtbaar uit.
    const busyRef = useRef(false)
    const [progress, setProgress] = useState({ written: 0, total: 0, soort: /** @type {'select' | 'deselect'} */ ('select') })
    const [melding, setMelding] = useState(/** @type {Melding | null} */ (null))
    const [opmerkingStraat, setOpmerkingStraat] = useState(/** @type {{ key: string, naam: string } | null} */ (null))
    const [confirm, setConfirm] = useState(/** @type {{ addresses: Array<object>, withFieldwork: number } | null} */ (null))

    const selectedKeys = useMemo(() => new Set(registraties.map((r) => r.Key)), [registraties])

    // Eén keer per lijst, niet per aanslag per adres.
    const zoekTeksten = useMemo(() => new Map(options.map((a) => [a.Key, zoekTekst(a)])), [options])

    const groups = useMemo(
        () => groupSelectionState({ addresses: options, selectedKeys, wijken, buurtNames }),
        [options, selectedKeys, wijken, buurtNames],
    )

    // Gekozen adressen die niet (meer) in de gekozen wijken liggen: een wijk
    // weghalen laat zijn rijen bewust staan. Pas te bepalen als de lijst compleet is.
    const elders = useMemo(() => {
        if (loadState !== 'complete') return []
        const inLijst = new Set(options.map((a) => a.Key))
        return registraties.filter((r) => !inLijst.has(r.Key)).sort(compareAddresses)
    }, [loadState, options, registraties])

    if (wijken.length === 0) return null

    const q = zoekterm.trim().toLowerCase()
    const searching = q !== ''
    const matches = (a) => (zoekTeksten.get(a.Key) ?? zoekTekst(a)).includes(q)
    const lijstCompleet = loadState === 'complete'
    const groepUit = !lijstCompleet || busy
    const adresUit = busy

    const runSelect = async (addresses) => {
        if (busyRef.current || addresses.length === 0) return
        busyRef.current = true
        setBusy(true)
        setMelding(null)
        setProgress({ written: 0, total: addresses.length, soort: 'select' })
        try {
            await onSelect(addresses, (written, total) => setProgress({ written, total, soort: 'select' }))
        } catch (err) {
            setMelding({ soort: 'select', tekst: err?.message || String(err) })
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }

    const runDeselect = async (addresses) => {
        if (busyRef.current || addresses.length === 0) return
        busyRef.current = true
        setBusy(true)
        setMelding(null)
        setProgress({ written: 0, total: addresses.length, soort: 'deselect' })
        try {
            await onDeselect(addresses, (written, total) => setProgress({ written, total, soort: 'deselect' }))
        } catch (err) {
            setMelding({ soort: 'deselect', tekst: err?.message || String(err) })
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }

    // Loslaten gaat ongevraagd, tenzij er bij de deur al iets is vastgelegd.
    const requestDeselect = (addresses) => {
        if (busyRef.current) return
        const { withFieldwork } = planDeselect(addresses, registraties)
        if (withFieldwork.length > 0) setConfirm({ addresses, withFieldwork: withFieldwork.length })
        else runDeselect(addresses)
    }

    /**
     * @param {Array<object>} addresses - Alle adressen van de groep
     * @param {SelectieStand} state - Huidige stand van de groep
     */
    const toggleGroup = (addresses, state) => {
        if (state === 'all') requestDeselect(addresses)
        else runSelect(addresses.filter((a) => !selectedKeys.has(a.Key)))
    }

    const toggleAdres = (a) => (selectedKeys.has(a.Key) ? requestDeselect([a]) : runSelect([a]))

    const toggleExpanded = (key) => setExpanded((prev) => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
    })

    const confirmDeselect = () => {
        const addresses = confirm?.addresses || []
        setConfirm(null)
        runDeselect(addresses)
    }

    const adresRij = (a, label) => (
        <div key={a.Key} className={`wa-address${selectedKeys.has(a.Key) ? ' wa-address-selected' : ''}`}>
            {canEdit && (
                <TriCheckbox
                    state={selectedKeys.has(a.Key) ? 'all' : 'none'}
                    label={fullAdresLabel(a)}
                    disabled={adresUit}
                    onClick={() => toggleAdres(a)}
                />
            )}
            <span>{label}</span>
        </div>
    )

    const flatten = (wijk) => wijk.buurten.flatMap((b) => b.straten.flatMap((s) => s.addresses))
    const flattenBuurt = (buurt) => buurt.straten.flatMap((s) => s.addresses)

    return (
        <div className='wa-step'>
            <h3>3. Adressen <span className='wa-sub'>{nl.format(selectedKeys.size)} geselecteerd</span></h3>

            {loadState === 'loading' && (
                <p className='wa-hint wa-loading'>
                    <svg className='wa-spinner' viewBox='0 0 24 24' width='16' height='16' aria-hidden='true'>
                        <circle cx='12' cy='12' r='9' fill='none' stroke='#e6e6e6' strokeWidth='3' />
                        <path d='M12 3 a9 9 0 0 1 9 9' fill='none' stroke='#47acc3' strokeWidth='3' strokeLinecap='round' />
                    </svg>
                    Adressen laden
                </p>
            )}
            {loadState === 'failed' && (
                <p className='wa-error' role='alert'>
                    De adressenlijst is niet volledig geladen. Kiezen per straat, buurt of wijk staat uit tot de
                    lijst compleet is. Herlaad de pagina om het opnieuw te proberen.
                </p>
            )}
            {busy && progress.total > 0 && (
                <p className='wa-hint' role='status'>
                    {`Bezig met ${progress.soort === 'deselect' ? 'loslaten' : 'opslaan'}: ${nl.format(progress.written)} van ${nl.format(progress.total)}`}
                </p>
            )}
            {melding?.soort === 'select' && (
                <p className='wa-error' role='alert'>
                    {`Opslaan is gestopt na ${nl.format(progress.written)} van ${nl.format(progress.total)} adressen (${melding.tekst}). `}
                    Klik nog een keer op het vinkje om de rest toe te voegen.
                </p>
            )}
            {melding?.soort === 'deselect' && (
                <p className='wa-error' role='alert'>
                    {`Loslaten is gestopt na ${nl.format(progress.written)} van ${nl.format(progress.total)} adressen (${melding.tekst}). `}
                    Klik nog een keer op het vinkje om de rest los te laten.
                </p>
            )}

            {options.length > 0 && (
                <div className='wa-search'>
                    <input
                        type='text'
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder='Zoek op straat, huisnummer of postcode'
                    />
                </div>
            )}

            <div className='wa-tree'>
                {groups.map((wijk) => (
                    <div key={wijk.code} className='wa-wijk'>
                        <div className='wa-row wa-row-wijk'>
                            {canEdit && (
                                <TriCheckbox
                                    state={wijk.state}
                                    label={`Hele wijk ${wijk.naam}`}
                                    disabled={groepUit}
                                    onClick={() => toggleGroup(flatten(wijk), wijk.state)}
                                />
                            )}
                            <span className='wa-row-name'>{wijk.naam}</span>
                            <Telling selected={wijk.selected} total={wijk.total} />
                        </div>

                        {wijk.buurten.map((buurt) => {
                            const straten = searching
                                ? buurt.straten.filter((s) => s.addresses.some(matches))
                                : buurt.straten
                            if (straten.length === 0) return null
                            const buurtNaam = buurt.naam || buurt.code
                            return (
                                <div key={buurt.code || 'overig'} className='wa-buurt'>
                                    <div className='wa-row wa-row-buurt'>
                                        {canEdit && (
                                            <TriCheckbox
                                                state={buurt.state}
                                                label={`Hele buurt ${buurtNaam}`}
                                                disabled={groepUit}
                                                onClick={() => toggleGroup(flattenBuurt(buurt), buurt.state)}
                                            />
                                        )}
                                        <span className='wa-row-name'>{buurtNaam}</span>
                                        <Telling selected={buurt.selected} total={buurt.total} />
                                    </div>

                                    {straten.map((straat) => {
                                        const key = `${wijk.code}|${buurt.code}|${straat.naam}`
                                        const open = searching || expanded.has(key)
                                        const zichtbaar = searching ? straat.addresses.filter(matches) : straat.addresses
                                        const opmerking = straatOpmerking({ StraatOpmerkingen: straatOpmerkingen }, straatKey(straat.addresses[0]))
                                        return (
                                            <div key={key} className='wa-straat'>
                                                <div className='wa-row wa-row-straat'>
                                                    {canEdit && (
                                                        <TriCheckbox
                                                            state={straat.state}
                                                            label={`Hele straat ${straat.naam}`}
                                                            disabled={groepUit}
                                                            onClick={() => toggleGroup(straat.addresses, straat.state)}
                                                        />
                                                    )}
                                                    <button
                                                        type='button'
                                                        className='wa-expand'
                                                        aria-expanded={open}
                                                        onClick={() => toggleExpanded(key)}
                                                    >
                                                        {open ? <ExpandMoreIcon aria-hidden='true' /> : <ChevronRightIcon aria-hidden='true' />}
                                                        <span className='wa-row-name'>{straat.naam}</span>
                                                    </button>
                                                    {canEdit && straatKey(straat.addresses[0]) && (
                                                        <button
                                                            type='button'
                                                            className='wa-opmerking-knop'
                                                            aria-label={opmerking ? `Opmerking bij ${straat.naam} bewerken` : `Opmerking bij ${straat.naam}`}
                                                            onClick={() => setOpmerkingStraat({ key: straatKey(straat.addresses[0]), naam: straat.naam })}
                                                        >
                                                            {opmerking ? 'Opmerking bewerken' : 'Opmerking'}
                                                        </button>
                                                    )}
                                                    <Telling selected={straat.selected} total={straat.total} />
                                                </div>
                                                {opmerking && <p className='wa-straat-opmerking'>{opmerking}</p>}
                                                {open && (
                                                    <div className='wa-addresses'>
                                                        {zichtbaar.map((a) => adresRij(a, adresLabel(a)))}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                ))}

                {lijstCompleet && options.length === 0 && <p className='wa-empty'>Geen adressen gevonden.</p>}
                {options.length > 0 && searching && !options.some(matches) && (
                    <p className='wa-empty'>Geen adressen voor “{zoekterm}”.</p>
                )}

                {elders.length > 0 && (
                    <div className='wa-elders'>
                        <div className='wa-row wa-row-buurt'>
                            <span className='wa-row-name'>Buiten de gekozen wijken</span>
                            <Telling selected={elders.length} total={elders.length} />
                        </div>
                        <div className='wa-addresses'>
                            {elders.map((r) => adresRij(r, fullAdresLabel(r)))}
                        </div>
                    </div>
                )}
            </div>

            <StraatOpmerkingModal
                open={!!opmerkingStraat}
                straat={opmerkingStraat?.naam || ''}
                beginTekst={opmerkingStraat ? straatOpmerking({ StraatOpmerkingen: straatOpmerkingen }, opmerkingStraat.key) : ''}
                onSave={(tekst) => onSaveOpmerking(opmerkingStraat.key, tekst)}
                onClose={() => setOpmerkingStraat(null)}
            />

            <Modal
                modalOpen={!!confirm}
                setModalOpen={(open) => { if (!open) setConfirm(null) }}
                confirm={confirmDeselect}
                confirmLabel='Loslaten'
                pageHeader='Adressen loslaten'
                content={confirm && (
                    <p>
                        {`Bij ${nl.format(confirm.withFieldwork)} van deze ${nl.format(confirm.addresses.length)} adressen is al iets ingevuld, zoals een brief, een gesprek of een antwoord. `}
                        Loslaten haalt ze uit de actie en uit de tellingen. Kies je ze later opnieuw, dan komt wat er
                        was ingevuld terug.
                    </p>
                )}
            />
        </div>
    )
}

export default AddressSelect
